import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';
import { emailService } from '../../../services/emailService';
import { notificationService } from '../../../services/notificationService';
import { Button, cn } from '../../../components/ui/Button';
import Toast from '../../../components/ui/Toast';
import { useToast } from '../../../hooks/useToast';

export default function BarberBookings() {
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelModal, setCancelModal] = useState({ isOpen: false, booking: null });
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const { toast, showToast, hideToast } = useToast();

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('bookings_1740423033529')
          .select(`
            *,
            customer:customer_id(id, full_name, email, phone),
            barber:barber_id(id, full_name, email),
            service:service_id(id, name, price)
          `)
          .eq('barber_id', user.id)
          .order('created_at', { ascending: false });
        if (error) throw error;
        setBookings(data || []);
      }
    } catch (err) {
      console.error("Booking fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    const closeMenu = () => setActiveMenu(null);
    window.addEventListener('click', closeMenu);
    return () => window.removeEventListener('click', closeMenu);
  }, []);

  const handleAction = async (e, booking, action) => {
    e.stopPropagation();

    if (action === 'Cancel') {
      setCancelModal({ isOpen: true, booking });
      setActiveMenu(null);
      return;
    }

    let newStatus = '';
    let extraUpdates = {};

    if (action === 'Accept') {
      newStatus = 'confirmed';
    } else if (action === 'Complete') {
      newStatus = 'completed';
      // Auto-set payment to paid since cash is collected on service completion
      extraUpdates.payment_status = 'paid';
    }

    if (!newStatus) return;

    try {
      const { error } = await supabase
        .from('bookings_1740423033529')
        .update({ status: newStatus, ...extraUpdates })
        .eq('id', booking.id);

      if (!error) {
        setBookings(prev => prev.map(b =>
          b.id === booking.id ? { ...b, status: newStatus, ...extraUpdates } : b
        ));

        const details = {
          customerEmail: booking.customer?.email,
          customerPhone: booking.customer?.phone,
          customerName: booking.customer?.full_name,
          barberName: booking.barber?.full_name || 'Your Barber',
          serviceName: booking.service?.name,
          date: booking.booking_date,
          time: booking.booking_time,
          price: booking.total_price,
          address: booking.address,
        };

        if (newStatus === 'confirmed' && booking.customer?.email) {
          const manageLink = `${window.location.origin}/booking/c/${booking.id}`;
          await emailService.sendBookingConfirmedToCustomer({ ...details, manageLink });
          await notificationService.notify(
            booking.customer_id,
            'Booking Confirmed!',
            `${details.barberName} approved your appointment.`,
            '/app/bookings'
          );
        }

        if (newStatus === 'completed' && booking.customer?.email) {
          const manageLink = `${window.location.origin}/booking/c/${booking.id}`;
          await emailService.sendBookingCompleted({ ...details, manageLink });
          await notificationService.notify(
            booking.customer_id,
            'Service Completed',
            `Thank you for choosing ${details.barberName}!`,
            '/app/bookings'
          );
        }

        showToast(`Booking marked as ${newStatus}.`, 'success');
      } else {
        showToast("Action failed: " + error.message, 'error');
      }
    } catch (err) {
      showToast("Action failed. Please try again.", 'error');
    } finally {
      setActiveMenu(null);
    }
  };

  const executeCancel = async () => {
    if (!cancelReason.trim()) {
      showToast("Please provide a cancellation reason.", 'error');
      return;
    }

    setCancelling(true);
    const booking = cancelModal.booking;

    try {
      const { error } = await supabase
        .from('bookings_1740423033529')
        .update({ status: 'cancelled', cancellation_reason: cancelReason.trim() })
        .eq('id', booking.id);

      if (!error) {
        setBookings(prev => prev.map(b =>
          b.id === booking.id ? { ...b, status: 'cancelled', cancellation_reason: cancelReason.trim() } : b
        ));

        const details = {
          customerEmail: booking.customer?.email,
          customerName: booking.customer?.full_name,
          barberName: booking.barber?.full_name || 'Your Barber',
          serviceName: booking.service?.name,
          date: booking.booking_date,
          time: booking.booking_time,
          price: booking.total_price,
          address: booking.address,
          reason: cancelReason.trim()
        };

        if (booking.customer?.email) {
          await emailService.sendBookingCancelled(
            { ...details, manageLink: `${window.location.origin}/booking/c/${booking.id}` },
            booking.customer.email
          );
        }

        await notificationService.notify(
          booking.customer_id,
          'Booking Cancelled',
          `Your appointment with ${details.barberName} was cancelled. Reason: ${cancelReason.trim()}`,
          '/app/bookings'
        );

        setCancelModal({ isOpen: false, booking: null });
        setCancelReason('');
        showToast('Booking cancelled successfully.', 'success');
      } else {
        showToast("Error cancelling booking.", 'error');
      }
    } catch (err) {
      showToast("Error cancelling booking.", 'error');
    } finally {
      setCancelling(false);
    }
  };

  const handleOpenMaps = (address) => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
  };

  return (
    <div className="animate-in fade-in duration-500">
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}

      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-black text-brand-dark tracking-tight">Appointments</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchBookings}
          disabled={loading}
          className="gap-2 px-4 h-10 border-gray-200 text-sm"
        >
          <SafeIcon name="RefreshCw" className={cn(loading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden min-h-[400px]">
        <div className="overflow-x-auto pb-[150px]">
          {loading ? (
            <div className="p-12 text-center text-gray-400 font-bold uppercase text-[10px] tracking-widest">
              Syncing schedule...
            </div>
          ) : bookings.length === 0 ? (
            <div className="p-20 text-center text-gray-400 italic">No bookings found.</div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 text-[10px] uppercase font-black tracking-widest">
                  <th className="px-8 py-5">Client details</th>
                  <th className="px-8 py-5">Service</th>
                  <th className="px-8 py-5">Schedule & Location</th>
                  <th className="px-8 py-5">Status</th>
                  <th className="px-8 py-5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {bookings.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-8 py-5">
                      <p className="font-black text-brand-dark text-sm">{b.customer?.full_name}</p>
                      <p className="text-[11px] text-gray-500 font-bold mt-0.5">{b.customer?.phone || 'No phone'}</p>
                    </td>
                    <td className="px-8 py-5 text-sm font-bold text-brand-primary">{b.service?.name || 'Removed'}</td>
                    <td className="px-8 py-5">
                      <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-bold text-gray-700">{b.booking_date} | {b.booking_time}</span>
                        <div
                          onClick={() => handleOpenMaps(b.address)}
                          className="flex items-start gap-1.5 mt-1 bg-brand-primary/5 p-2 rounded-lg border border-brand-primary/10 cursor-pointer hover:bg-brand-primary/10 transition-colors group"
                        >
                          <SafeIcon name="MapPin" className="text-brand-primary text-[14px] mt-0.5 flex-shrink-0" />
                          <div className="flex flex-col">
                            <p className="text-[10px] text-gray-600 font-medium line-clamp-1">{b.address}</p>
                            <span className="text-[8px] font-black uppercase text-brand-primary group-hover:underline">Open Navigation</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <span className={cn(
                        "px-3 py-1 rounded-full text-[10px] font-black uppercase border",
                        b.status === 'confirmed' ? 'bg-blue-50 text-blue-600 border-blue-100' :
                        b.status === 'completed' ? 'bg-green-50 text-green-600 border-green-100' :
                        b.status === 'cancelled' ? 'bg-red-50 text-red-600 border-red-100' :
                        'bg-gray-50 text-gray-400 border-gray-100'
                      )}>
                        {b.status}
                      </span>
                    </td>
                    <td className="px-8 py-5 text-right relative">
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === b.id ? null : b.id); }}
                        className="p-2 text-gray-400 hover:text-brand-dark transition-colors"
                      >
                        <SafeIcon name="MoreVertical" />
                      </button>

                      {activeMenu === b.id && (
                        <div className="absolute right-8 top-12 w-44 bg-white shadow-2xl rounded-2xl border border-gray-100 z-50 overflow-hidden py-2 animate-in slide-in-from-top-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/booking/b/${b.id}`); }}
                            className="w-full text-left px-4 py-3 text-xs font-black text-gray-600 hover:bg-gray-50 flex items-center gap-2 border-b border-gray-50"
                          >
                            <SafeIcon name="ExternalLink" className="text-sm" />
                            Manage Live
                          </button>
                          {b.status === 'pending' && (
                            <button
                              onClick={(e) => handleAction(e, b, 'Accept')}
                              className="w-full text-left px-4 py-3 text-xs font-black text-blue-600 hover:bg-blue-50 flex items-center gap-2"
                            >
                              <SafeIcon name="Check" className="text-sm" />
                              Approve
                            </button>
                          )}
                          {b.status !== 'completed' && b.status !== 'cancelled' && (
                            <>
                              <button
                                onClick={(e) => handleAction(e, b, 'Complete')}
                                className="w-full text-left px-4 py-3 text-xs font-black text-green-600 hover:bg-green-50 flex items-center gap-2"
                              >
                                <SafeIcon name="CheckCircle" className="text-sm" />
                                Complete
                              </button>
                              <button
                                onClick={(e) => handleAction(e, b, 'Cancel')}
                                className="w-full text-left px-4 py-3 text-xs font-black text-red-600 hover:bg-red-50 flex items-center gap-2"
                              >
                                <SafeIcon name="XCircle" className="text-sm" />
                                Cancel
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Cancel Modal */}
      {cancelModal.isOpen && (
        <div className="fixed inset-0 bg-brand-dark/60 z-50 flex items-center justify-center p-6 backdrop-blur-sm">
          <div className="bg-white rounded-[2rem] p-8 max-w-sm w-full shadow-2xl animate-in zoom-in-95">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon name="XCircle" className="text-3xl" />
            </div>
            <h2 className="text-xl font-black text-brand-dark mb-2 text-center">Decline Appointment</h2>
            <p className="text-gray-500 font-medium text-sm mb-6 text-center leading-relaxed">
              Please let the client know why you are cancelling this appointment.
            </p>
            <textarea
              placeholder="Reason for cancellation (required)..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl mb-6 text-sm font-medium outline-none focus:border-red-500 min-h-[100px]"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setCancelModal({ isOpen: false, booking: null }); setCancelReason(''); }}
              >
                Go Back
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                disabled={!cancelReason.trim() || cancelling}
                onClick={executeCancel}
              >
                {cancelling ? 'Cancelling...' : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}